import { useState } from "react";
import { Zap } from "lucide-react";
import { formatNumber } from "@/lib/formatters";

export const RENTENIVAA = 5.3; // KLP si budsjettrente for 2026: 4,7 % NIBOR + 0,6 marginpåslag

const kr = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} kr`);
const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const pst = (v, d = 1) => (v == null ? "—" : `${v.toFixed(d).replace(".", ",")} %`);

export const Rentesjokk = ({ data }) => {
  const [rente, setRente] = useState(RENTENIVAA);
  const latest = data?.latest;
  const gjeld = latest?.laanegjeld;
  const folk = latest?.population;
  if (!gjeld || !folk || !latest?.renteutgifter) return null;

  const dagensRente = latest.rente_pct;
  const nyKostnad = (gjeld * rente) / 100;
  const auke = nyKostnad - latest.renteutgifter;
  const aukePerInnb = (auke * 1000) / folk;
  const delAvInntekter = latest.driftsinntekter ? (auke / latest.driftsinntekter) * 100 : null;
  const aarsverk = Math.round((auke * 1000) / 850000); // 850 000 kr i gjennomsnittleg årsverkskostnad
  const opp = auke >= 0;

  return (
    <div className="p-4 border border-[#E6E9F0] rounded-lg" data-testid="rentesjokk-block">
      <div className="flex items-start gap-2 mb-3">
        <Zap className="w-4 h-4 text-[#8AA4BF] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Rentesjokk</p>
          <p className="text-xs text-[#8AA4BF] mt-0.5">
            Kva ville lånegjelda kosta om heile gjelda blei prisa til dagens rentenivå? Kommunen betaler{" "}
            <span className="font-semibold text-[#1F2A36]">{pst(dagensRente, 2)}</span> i snitt i dag, mens budsjettrenta for 2026 ligg kring{" "}
            {pst(RENTENIVAA, 1)}.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <p className="text-xs font-medium text-[#1F2A36]">
          Rentenivå <span className="text-[#8AA4BF]">· {mill(gjeld)} i lånegjeld</span>
        </p>
        <p className="text-xs text-[#4B647F]">
          Rente:{" "}
          <span className="font-semibold text-[#C0392B]" data-testid="rentesjokk-rente">
            {pst(rente, 1)}
          </span>
        </p>
      </div>
      <input
        type="range"
        min={2}
        max={9}
        step={0.1}
        value={rente}
        onChange={(e) => setRente(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none bg-[#E6E9F0] accent-[#C0392B] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#C0392B]/30"
        data-testid="rentesjokk-slider"
        aria-label="Rentenivå i prosent"
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <div className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
          <p className="text-[10px] text-[#8AA4BF] leading-tight">Renteutgifter i dag</p>
          <p className="text-sm text-[#1F2A36] tabular-nums">{mill(latest.renteutgifter)}</p>
        </div>
        <div className={`rounded-lg px-2 py-1.5 ${opp ? "bg-[#FBEAE7]" : "bg-[#F7F9FC]"}`}>
          <p className="text-[10px] text-[#8AA4BF] leading-tight">Med {pst(rente, 1)}</p>
          <p className="text-sm font-medium tabular-nums text-[#C0392B]" data-testid="rentesjokk-ny-kostnad">
            {mill(nyKostnad)}
          </p>
        </div>
        <div className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
          <p className="text-[10px] text-[#8AA4BF] leading-tight">Endring per innbyggjar</p>
          <p className="text-sm text-[#1F2A36] tabular-nums" data-testid="rentesjokk-per-innb">
            {opp ? "+" : ""}
            {kr(aukePerInnb)}
          </p>
        </div>
        <div className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
          <p className="text-[10px] text-[#8AA4BF] leading-tight">Del av driftsinntektene</p>
          <p className="text-sm text-[#1F2A36] tabular-nums">
            {delAvInntekter == null ? "—" : `${opp ? "+" : ""}${pst(delAvInntekter, 2)}`}
          </p>
        </div>
      </div>

      <p className="text-[11px] text-[#8AA4BF] mt-2" data-testid="rentesjokk-tekst">
        {opp
          ? `Ei rente på ${pst(rente, 1)} ville kosta ${mill(auke)} meir i året enn i dag – ${kr(aukePerInnb)} per innbyggjar, eller om lag ${formatNumber(Math.abs(aarsverk))} årsverk i tenestene.`
          : `Ei rente på ${pst(rente, 1)} ville vere ${mill(Math.abs(auke))} billegare i året enn i dag – ${kr(Math.abs(aukePerInnb))} per innbyggjar.`}
      </p>
    </div>
  );
};

export default Rentesjokk;
